import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Who We Work With", path: "/who-we-work-with" },
    { name: "Case Studies", path: "/case-studies" },
    { name: "Insights", path: "/insights" },
    { name: "Contact", path: "/contact" }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
          isScrolled ? "w-[95%] max-w-7xl" : "w-[90%] max-w-6xl"
        }`}
      >
        <div
          className={`glass-card rounded-full px-6 py-4 transition-all duration-300 ${
            isScrolled ? "shadow-2xl" : ""
          }`}
        >
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="font-heading font-bold text-xl text-white hover:text-brandPrimary transition-colors"
              data-testid="nav-logo"
            >
              Media Points Group
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-sm font-medium transition-colors hover:text-brandPrimary ${
                    location.pathname === link.path
                      ? "text-brandPrimary"
                      : "text-gray-300"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="nav-cta-button"
                className="bg-brandPrimary hover:bg-brandPrimary/90 text-white px-6 py-2.5 rounded-full font-medium transition-all hover:shadow-lg hover:shadow-brandPrimary/25"
              >
                Book Strategy Call
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-28 left-1/2 -translate-x-1/2 w-[90%] max-w-md z-40 lg:hidden"
          >
            <div className="glass-card rounded-3xl p-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block text-base font-medium transition-colors hover:text-brandPrimary py-2 ${
                    location.pathname === link.path
                      ? "text-brandPrimary"
                      : "text-gray-300"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-cta-button"
                className="block w-full text-center bg-brandPrimary hover:bg-brandPrimary/90 text-white px-6 py-3 rounded-full font-medium transition-all mt-4"
              >
                Book Strategy Call
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;